import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {FmlxButton, FmlxIcon, FmlxTooltip} from 'fmlx-common-ui';

const regexInt = /^(-?)+[0-9\b]*$/;
const regexUInt = /^[0-9\b]+$/;
const regexDecimal = /^(-?)+[0-9]*([.]?[0-9]+[.]?)?$/;
const regexUDecimal = /^[0-9]*([.]?[0-9]+[.]?)?$/;

export default class FmlxNumericInputWithButton extends Component {
  constructor(props) {
    super(props);
    this.state = {
      inputValue: parseFloat(props.defaultValue),
    };
  }

  componentDidUpdate(prevProps) {
    const {value} = this.props;
    if (value !== prevProps.value) {
      this.updateInputValue(value);
    }
  }

  handlerOnChange(value) {
    const {allowDecimal, allowNegativeNumber, max, min, decimalPrecision} = this.props;
    let inputValue = value.toString();

    if (inputValue === '') {
      this.updateInputValue(inputValue);

      return;
    }

    if (inputValue === '00') {
      this.updateInputValue('0');

      return;
    }

    if (allowDecimal) {
      if (allowNegativeNumber) {
        if (regexDecimal.test(inputValue) && inputValue.split('.').length <= 2) {
          const isValidMax = max ? parseFloat(inputValue) <= parseFloat(max) : true;
          const isValidMin = min ? parseFloat(inputValue) >= parseFloat(min) : isValidMax;

          if (isValidMax && isValidMin) {
            if (inputValue.split('.')[1]?.length > decimalPrecision) {
              inputValue = parseFloat(value).toFixed(decimalPrecision);
            }
          } else if (!isValidMax) {
            inputValue = parseFloat(max);
          } else if (!isValidMin) {
            inputValue = parseFloat(min);
          }

          this.updateInputValue(inputValue);
        }

        return;
      }

      if (regexUDecimal.test(inputValue) && inputValue.split('.').length <= 2) {
        const isValidMax = max ? parseFloat(inputValue) <= parseFloat(max) : true;
        const isValidMin = min ? parseFloat(inputValue) >= parseFloat(min) : isValidMax;

        if (isValidMax && isValidMin) {
          if (inputValue.split('.')[1]?.length > decimalPrecision) {
            inputValue = parseFloat(value).toFixed(decimalPrecision);
          }
        } else if (!isValidMax) {
          inputValue = parseFloat(max);
        } else if (!isValidMin) {
          inputValue = parseFloat(min);
        }

        this.updateInputValue(inputValue);
      }

      return;
    }

    if (allowNegativeNumber) {
      if (regexInt.test(inputValue)) {
        const isValidMax = max ? parseFloat(inputValue) <= parseFloat(max) : true;
        const isValidMin = min ? parseFloat(inputValue) >= parseFloat(min) : isValidMax;

        if (!isValidMax) {
          inputValue = parseFloat(max);
        } else if (!isValidMin) {
          inputValue = parseFloat(min);
        }

        this.updateInputValue(inputValue);
      }

      return;
    }

    if (regexUInt.test(inputValue)) {
      const isValidMax = max ? parseFloat(inputValue) <= parseFloat(max) : true;
      const isValidMin = min ? parseFloat(inputValue) >= parseFloat(min) : isValidMax;

      if (!isValidMax) {
        inputValue = parseFloat(max);
      } else if (!isValidMin) {
        inputValue = parseFloat(min);
      }

      this.updateInputValue(inputValue);
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleTriggerValueChanged(value) {
    this.handlerOnChange(parseFloat(value));
  }

  handleClickPlusButton() {
    const {inputValue} = this.state;
    const {stepValue} = this.props;

    const parsedValue = Number.isNaN(parseFloat(inputValue)) ? 0 : parseFloat(inputValue);

    this.handlerOnChange(parsedValue + parseFloat(stepValue));
  }

  handleClickMinusButton() {
    const {inputValue} = this.state;
    const {stepValue} = this.props;

    const parsedValue = Number.isNaN(parseFloat(inputValue)) ? 0 : parseFloat(inputValue);

    this.handlerOnChange(parsedValue - parseFloat(stepValue));
  }

  updateInputValue(newValue) {
    const {inputValue} = this.state;
    const {onChange} = this.props;

    if (newValue !== inputValue) {
      this.setState({inputValue: newValue});
      onChange(newValue);
    }
  }

  render() {
    const {inputValue} = this.state;
    const {minusButtonClassName, inputClassName, placeholder, onBlur, onFocus, plusButtonClassName, tooltip} =
      this.props;

    return (
      <div style={{display: 'flex', flexDirection: 'row', gap: 10}}>
        <FmlxButton
          className={minusButtonClassName}
          onClick={() => this.handleClickMinusButton()}
          onlyIcon
          icon={<FmlxIcon name="MinusOutline" />}
          withTooltip={false}
        />
        {tooltip ? (
          <FmlxTooltip title={tooltip}>
            <span>
              <input
                className={inputClassName}
                placeholder={placeholder}
                value={inputValue}
                onBlur={onBlur ? (event) => onBlur(event) : null}
                onChange={(event) => this.handlerOnChange(event.target.value)}
                onFocus={onFocus ? (event) => onFocus(event) : null}
                onKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    e.preventDefault(); // Prevent default form submission behavior
                    e.target.blur(); // Blur the input box
                  }
                }}
              />
            </span>
          </FmlxTooltip>
        ) : (
          <input
            className={inputClassName}
            placeholder={placeholder}
            value={inputValue}
            onBlur={onBlur ? (event) => onBlur(event) : null}
            onChange={(event) => this.handlerOnChange(event.target.value)}
            onFocus={onFocus ? (event) => onFocus(event) : null}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                e.preventDefault(); // Prevent default form submission behavior
                e.target.blur(); // Blur the input box
              }
            }}
          />
        )}
        <FmlxButton
          className={plusButtonClassName}
          onClick={() => this.handleClickPlusButton()}
          onlyIcon
          icon={<FmlxIcon name="Add" />}
          withTooltip={false}
        />
      </div>
    );
  }
}

FmlxNumericInputWithButton.propTypes = {
  allowDecimal: PropTypes.bool,
  allowNegativeNumber: PropTypes.bool,
  defaultValue: PropTypes.number,
  value: PropTypes.number,
  max: PropTypes.number,
  min: PropTypes.number,
  stepValue: PropTypes.number,
  decimalPrecision: PropTypes.number,
  placeholder: PropTypes.string,
  inputClassName: PropTypes.string,
  plusButtonClassName: PropTypes.string,
  minusButtonClassName: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  tooltip: PropTypes.node,
};

FmlxNumericInputWithButton.defaultProps = {
  allowDecimal: false,
  allowNegativeNumber: false,
  defaultValue: 0,
  value: 0,
  max: 9999,
  min: 0,
  stepValue: 1,
  decimalPrecision: 1,
  placeholder: '0',
  inputClassName: '',
  plusButtonClassName: '',
  minusButtonClassName: '',
  onBlur: () => {},
  onChange: () => {},
  onFocus: () => {},
  tooltip: null,
};
